import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Loader2, Link } from 'lucide-react';
import { Button }   from '@/components/ui/button';
import { Input }    from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import API_BASE from '@/lib/API_BASE';


const contactInfo = [
  { icon: Phone,  label: 'Phone',         values: ['+91 93916 50077', '+91 98855 89762'], sub: 'Mon–Sat, 9 AM – 7 PM'  },
  { icon: Mail,   label: 'Email',         value: 'info@xceededge.com', sub: 'Reply within 24 hours'  },
  { icon: MapPin, label: 'Office',        value: 'Hyderabad, Telangana',  sub: 'India – 560001'         },
  { icon: Clock,  label: 'Support Hours', value: '9 AM – 9 PM',          sub: 'Monday to Saturday'     },
];

const EMPTY_FORM = { name: '', email: '', phone: '', subject: '', message: '' };

export default function Contact() {
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [errors,    setErrors]    = useState([]);  // server-side errors

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors([]); // clear errors on edit
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    const fd = new FormData();
    fd.append('name',    form.name);
    fd.append('email',   form.email);
    fd.append('phone',   form.phone);
    fd.append('subject', form.subject);
    fd.append('message', form.message);

    axios.post(`${API_BASE}/contact.php`, fd)
      .then(res => {
        if (res.data.success) {
          setSubmitted(true);
          setForm(EMPTY_FORM);
        } else {
          setErrors(res.data.errors || ['Something went wrong.']);
        }
      })
      .catch(() => setErrors(['Server error. Please try again.']))
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen bg-background">

      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
              Get In Touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              We're Here to <span className="text-primary">Help You</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Have a question about our courses, admissions, or placement support? Our team is ready to assist you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12">

          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl font-bold mb-6">Contact Information</h2>
            <div className="space-y-4 mb-8">
              {contactInfo.map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-card rounded-xl border">
                  <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{item.label}</p>
                    {/* `values` lets one card list several entries (e.g. two phone numbers) */}
                    {(item.values ?? [item.value]).map((v, j) => (
                      <p key={j} className="font-semibold text-foreground">{v}</p>
                    ))}
                    <p className="text-sm text-muted-foreground">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold">Free Career Counselling</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Not sure which course is right for you? Book a free 30-minute counselling session with our experts.
              </p>
            <Button
              className="w-full"
              onClick={() => window.open('https://wa.me/919391650077?text=Hi%20I%20want%20a%20Free%20Career%20Plan%20Session', '_blank')}
            >
              Book Free Session
            </Button>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="bg-card rounded-2xl border p-8">
              {submitted ? (
                // ── Success state ──────────────────────────────────────────
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground">We'll get back to you within 24 hours.</p>
                  <Button variant="outline" className="mt-6" onClick={() => setSubmitted(false)}>
                    Send Another
                  </Button>
                </div>
              ) : (
                // ── Form ──────────────────────────────────────────────────
                <>
                  <h2 className="font-heading text-2xl font-bold mb-6">Send Us a Message</h2>

                  {/* Server error list */}
                  {errors.length > 0 && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
                      {errors.map((err, i) => (
                        <p key={i} className="text-sm text-red-600">{err}</p>
                      ))}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1.5 block">Full Name *</label>
                        <Input
                          name="name"
                          placeholder="Your full name"
                          value={form.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1.5 block">Phone Number</label>
                        <Input
                          name="phone"
                          placeholder="+91 9391650077"
                          value={form.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block">Email Address *</label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Enter Your Email"
                        value={form.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block">Subject</label>
                      <Input
                        name="subject"
                        placeholder="Course inquiry, placement support, etc."
                        value={form.subject}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1.5 block">Message *</label>
                      <Textarea
                        name="message"
                        placeholder="Tell us how we can help you..."
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={loading}
                    >
                      {loading
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                        : <><Send className="w-4 h-4 mr-2" /> Send Message</>
                      }
                    </Button>
                  </form>
                </>
              )}
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
}